import { Input, Popover, PopoverAnchor, PopoverContent } from '@ibsys/kit';
import { PencilIcon } from 'lucide-react';
import { FC, KeyboardEvent, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton } from '@/shared/ui';

type Props = {
  /** Текущее название пресета */
  name: string;
  onRename: (name: string) => void;
};

/**
 * Переименование активного пресета: имя правится в окне у кнопки и записывается при его закрытии.
 * Escape закрывает окно, ничего не записывая.
 */
export const PresetRenamePopover: FC<Props> = ({ name, onRename }) => {
  const { t } = useTranslation('tablePresets');
  const [isOpen, setIsOpen] = useState(false);
  const [draftName, setDraftName] = useState(name);
  const isCancelledRef = useRef(false);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      if (open) {
        setDraftName(name);
        isCancelledRef.current = false;
        return;
      }
      if (isCancelledRef.current) return;
      const trimmed = draftName.trim();
      if (trimmed && trimmed !== name) {
        onRename(trimmed);
      }
    },
    [name, draftName, onRename],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleOpenChange(false);
        return;
      }
      // Само закрытие по Escape делает окно — здесь только отмечаем, что записывать нечего
      if (e.key === 'Escape') {
        isCancelledRef.current = true;
      }
    },
    [handleOpenChange],
  );

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      {/* Кнопка держит подсказку сама, поэтому окно привязано к якорю, а не к триггеру:
          вложенный в триггер тултип на касании подменяется своим поповером. */}
      <PopoverAnchor>
        <IconButton
          size="icon"
          className="shrink-0"
          label={t('rename')}
          icon={<PencilIcon className="size-4" />}
          onClick={() => handleOpenChange(true)}
        />
      </PopoverAnchor>
      <PopoverContent align="end" className="w-64 p-3">
        <Input
          autoFocus
          size="sm"
          value={draftName}
          onChange={(e) => setDraftName(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('newPresetPlaceholder')}
        />
      </PopoverContent>
    </Popover>
  );
};
